import type { VisitableGraph } from '../interface';
import type { EID, NID } from '../types';
import { ControlFlow } from './ControlFlow';
import { depthFirstSearch, type DFSVisitor } from './depthFirstSearch';

//
// pathExists
//

export function pathExists<G, N, E>(
  graph: VisitableGraph<G, N, E>,
  from: NID,
  to: NID,
  excludedEdge?: EID
): boolean {
  return depthFirstSearch(graph, new PathExistsVisitor<G, N, E>(from, to), {
    roots: new Set([from]),
    excludedEdge
  });
}

class PathExistsVisitor<G, N, E> implements DFSVisitor<G, N, E, boolean> {
  constructor(
    private from: NID,
    private to: NID
  ) {}

  discoverNode(graph: VisitableGraph<G, N, E>, node: NID): ControlFlow<boolean> {
    if (node === this.to) {
      return ControlFlow.break(true);
    } else {
      return ControlFlow.continue();
    }
  }

  finishNode(graph: VisitableGraph<G, N, E>, node: NID): ControlFlow<boolean> {
    if (node === this.from) {
      return ControlFlow.break(false);
    } else {
      return ControlFlow.continue();
    }
  }
}

export function canAddDAGEdge<G, N, E>(
  graph: VisitableGraph<G, N, E>,
  from: NID,
  to: NID,
  excludedEdge?: EID
): boolean {
  return !pathExists(graph, to, from, excludedEdge);
}

export function canMoveDAGEdge<G, N, E>(
  graph: VisitableGraph<G, N, E>,
  edge: EID,
  ends?: { newSource?: NID; newTarget?: NID }
): boolean {
  const { newSource, newTarget } = ends ?? {};
  const source = newSource ?? graph.source(edge);
  const target = newTarget ?? graph.target(edge);
  return canAddDAGEdge(graph, source, target, edge);
}
